import { useNuxtApp } from '#app';
import { BaseService } from '../base/base.service';
import type { IModelResponse } from '../base/dto/base.dto';
import {
  ModelKnowledgeCategory,
  ModelKnowledgeDetail,
  ModelApplyFileStatus,
  ModelSaveLink,
  ModelValidateLink,
  type IModelCreateKnowledgeCategoryRequest,
} from './dto/knowledge-base.dto';
import { KnowledgeBaseRepository } from './knowledge-base.repository';

export class KnowledgeBaseService extends BaseService {
  private knowledgeBaseRepository = new KnowledgeBaseRepository(
    this.api,
    this.configService
  );

  async getKnowledgeCategory(
    params: any
  ): Promise<IModelResponse<ModelKnowledgeCategory[]>> {
    let dataResult: IModelResponse<ModelKnowledgeCategory[]> = {
      data: [],
    };
    const { $functions } = useNuxtApp();
    try {
      const res =
        await this.knowledgeBaseRepository.getKnowledgeCategory(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = (resData.data || []).map(
          (e) => new ModelKnowledgeCategory(e)
        );
        dataResult.data = dataList;
        dataResult.totalItems = resData.totalItems;
        console.log('getKnowledgeCategory', resData.data, dataResult);
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async createKnowledgeCategory(
    params: IModelCreateKnowledgeCategoryRequest
  ): Promise<ModelKnowledgeCategory | undefined> {
    let dataResult;
    const { $functions } = useNuxtApp();
    try {
      const res =
        await this.knowledgeBaseRepository.createKnowledgeCategory(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelKnowledgeCategory(resData.data);
        console.log('createKnowledgeCategory', resData.data, dataResult);
      }
    } catch (err: any) {
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async updateKnowledgeCategory(
    params: IModelCreateKnowledgeCategoryRequest
  ): Promise<ModelKnowledgeCategory | undefined> {
    let dataResult;
    const { $functions } = useNuxtApp();
    try {
      const res =
        await this.knowledgeBaseRepository.updateKnowledgeCategory(params);

      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        dataResult = new ModelKnowledgeCategory(resData.data);
        console.log('updateKnowledgeCategory', resData.data, dataResult);
      }
    } catch (err: any) {
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async deleteKnowledgeCategory(id: string): Promise<any> {
    const { $functions } = useNuxtApp();
    let dataResult;

    try {
      const res =
        await this.knowledgeBaseRepository.deleteKnowledgeCategory(id);
      const resData = res?.data?.value;
      if (resData && $functions.isValidResponse(resData)) {
        dataResult = resData.data;
        console.log('deleteKnowledgeCategory', id, resData.data, dataResult);
      }
    } catch (err: any) {
      const errorMessage =
        err?.message || err?.response?._data?.message || 'An error occurred';
      $functions.notify.error({
        text: errorMessage,
      });
      console.log(err, KnowledgeBaseService.name);
    }

    return dataResult;
  }

  async deleteKnowledge(id: string): Promise<any> {
    const { $functions } = useNuxtApp();
    let dataResult;
    try {
      const res = await this.knowledgeBaseRepository.deleteKnowledge(id);
      const resData = res?.data?.value;
      if (resData && $functions.isValidResponse(resData)) {
        dataResult = resData.data;
        console.log('deleteKnowledge', id, resData.data, dataResult);
      }
    } catch (err: any) {
      const errorMessage =
        err?.message || err?.response?._data?.message || 'An error occurred';
      $functions.notify.error({ text: errorMessage });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async updateKnowledge(params: any): Promise<any> {
    const { $functions } = useNuxtApp();
    let dataResult;
    try {
      const res = await this.knowledgeBaseRepository.updateKnowledge(params);
      const resData = res?.data?.value;
      if (resData && $functions.isValidResponse(resData)) {
        dataResult = resData.data;
      }
    } catch (err: any) {
      const errorMessage =
        err?.message || err?.response?._data?.message || 'An error occurred';
      $functions.notify.error({ text: errorMessage });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async getKnowledgeDetail(
    view: 'knowledge-document' | 'knowledge-preset',
    params: any
  ): Promise<IModelResponse<ModelKnowledgeDetail[]>> {
    let dataResult: IModelResponse<ModelKnowledgeDetail[]> = {
      data: [],
    };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.knowledgeBaseRepository.getKnowledgeDetail(
        view,
        params
      );
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = (resData.data || []).map(
          (e) => new ModelKnowledgeDetail(e)
        );
        dataResult.data = dataList;
        dataResult.totalItems = resData.totalItems;
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async uploadKnowledgeFile(params: FormData): Promise<any> {
    const { $functions } = useNuxtApp();
    let dataResult;

    try {
      const res =
        await this.knowledgeBaseRepository.uploadKnowledgeFile(params);
      const resData = res.data.value;
      dataResult = resData;
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
      });
      console.log(err, KnowledgeBaseService.name);
    }

    return dataResult;
  }

  async getApplyFileStatus(
    params: any
  ): Promise<IModelResponse<ModelApplyFileStatus>> {
    let dataResult: IModelResponse<ModelApplyFileStatus> = {
      data: new ModelApplyFileStatus(),
    };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.knowledgeBaseRepository.getApplyFileStatus(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = new ModelApplyFileStatus(resData.data);
        dataResult.data = dataList;
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async applyFile(params: any): Promise<IModelResponse<any>> {
    let dataResult: IModelResponse<any> = {
      data: {},
    };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.knowledgeBaseRepository.applyFile(params);
      const resData = res.data.value;
      const dataList = resData;
      dataResult.data = dataList;
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async downloadKnowledgeFile(id: string): Promise<any> {
    let dataResult;
    try {
      const res = await this.knowledgeBaseRepository.downloadKnowledgeFile(id);
      const resData = res.data.value;
      if (resData) {
        dataResult = resData;
      }
    } catch (err: any) {
      const { $functions } = useNuxtApp();
      $functions.notify.error({
        text: err.message,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async onSaveLink(params: any): Promise<IModelResponse<ModelSaveLink>> {
    let dataResult: IModelResponse<ModelSaveLink> = {
      data: new ModelSaveLink(),
    };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.knowledgeBaseRepository.onSaveLink(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = new ModelSaveLink(resData.data);
        dataResult.data = dataList;
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async getApplyLinkStatus(
    params: any
  ): Promise<IModelResponse<ModelApplyFileStatus>> {
    let dataResult: IModelResponse<ModelApplyFileStatus> = {
      data: new ModelApplyFileStatus(),
    };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.knowledgeBaseRepository.getApplyLinkStatus(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = new ModelApplyFileStatus(resData.data);
        dataResult.data = dataList;
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async applyLink(params: any): Promise<IModelResponse<any>> {
    let dataResult: IModelResponse<any> = {
      data: {},
    };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.knowledgeBaseRepository.applyLink(params);
      const resData = res.data.value;
      const dataList = resData;
      dataResult.data = dataList;
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }

  async validateLink(params: any): Promise<IModelResponse<ModelValidateLink>> {
    let dataResult: IModelResponse<ModelValidateLink> = {
      data: new ModelValidateLink(),
    };
    const { $functions } = useNuxtApp();
    try {
      const res = await this.knowledgeBaseRepository.validateLink(params);
      const resData = res.data.value;
      if ($functions.isValidResponse(resData)) {
        const dataList = new ModelValidateLink(resData.data);
        dataResult.data = dataList;
      }
    } catch (err: any) {
      $functions.notify.error({
        text: err.message,
        isError: true,
      });
      console.log(err, KnowledgeBaseService.name);
    }
    return dataResult;
  }
}
